import React, { useEffect, useState } from "react";
import { Button, Card, Col, Form, Input, message, Row } from "antd";
import { LockOutlined, MailOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import { deleteDoc, doc, serverTimestamp, setDoc, updateDoc } from "firebase/firestore";
import { auth, firestore } from "../../../../configs/FirebaseConfig";
import { createUserWithEmailAndPassword, sendEmailVerification, signOut } from "firebase/auth";
import urls, { login } from "urls";
import { COMPANY_CONSTANTS } from "../../../../constants/CompanyConstants";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useInvitedUsers } from "../../../../data/hooks/firestore/useInvitedUsers";
import Loading from "../../../../components/shared-components/Loading";

const backgroundStyle = {
  backgroundImage: "url(/img/others/img-17.jpg)",
  backgroundRepeat: "no-repeat",
  backgroundSize: "cover",
};

const AcceptInvite = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const customerName = "Test";
  const customerId = "2";
  const userBanned = false;
  const userFavorites = [];
  const userShoppingCart = [];
  const userStatus = "active";
  const userEmailVerified = false;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [validLink, setValidLink] = useState(true);
  const [invitedEmail, setInvitedEmail] = useState("");
  const [invitedUserId, setInvitedUserId] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { data: invitedUsers, loading: invitedUsersLoading } = useInvitedUsers();
  const [invitationFound, setInvitationFound] = useState(false);
  const { encodedLink } = useParams();
  const inviteLink = encodedLink;

  const decodeLink = (link) => {
    try {
      return atob(link);
    } catch (err) {
      return "";
    }
  };

  useEffect(() => {
    if (!invitationFound && !invitedUsersLoading && invitedUsers) {
      let decodedLink = decodeLink(inviteLink);
      let parts = decodedLink.split("/");
      let id = parts[0];
      let email = parts[1];
      setInvitedUserId(id);
      setInvitedEmail(email);
      if (!!invitedUsers) {
        let validLink = _.findIndex(invitedUsers, ["id", id]) >= 0;
        if (!validLink) {
          setValidLink(false);
        } else setInvitationFound(true);
      }
    }
  }, [invitedUsers, invitedUsersLoading, invitationFound]);

  const onSubmit = () => {
    let formEmailMatchesInvite = invitedEmail === email;
    let role = _.find(invitedUsers, ["id", invitedUserId]).userRole;
    let userFirstName = _.find(invitedUsers, ["id", invitedUserId]).userFirstName;
    let userLastName = _.find(invitedUsers, ["id", invitedUserId]).userLastName;
    let userPhoneNumber = _.find(invitedUsers, ["id", invitedUserId]).userPhoneNumber;
    let userRegion = _.find(invitedUsers, ["id", invitedUserId]).userRegion;
    if (formEmailMatchesInvite && !!role) {
      setLoading(true);
      createUserWithEmailAndPassword(auth, email, password).then(async (userCredential) => {
        // Create user profile in Firestore
        const user = userCredential.user;
        await setDoc(doc(firestore, "users", user.uid), {
          userEmail: email,
          userRole: role,
          userId: invitedUserId,
          userFirstName: userFirstName,
          userLastName: userLastName,
          userCustomerName: customerName,
          userCustomerId: customerId,
          userPhoneNumber: userPhoneNumber,
          userStatus: userStatus,
          userShoppingCart: userShoppingCart,
          userFavorites: userFavorites,
          userBanned: userBanned,
          userRegion: userRegion,
          userEmailVerified: userEmailVerified,
          created_at: serverTimestamp(),
        });
        const actionCodeSettings = {
          url: urls.base + urls.login,
        };
        // Update user object with id
        updateUserInFirestore(user.uid);
        // Send verification email
        sendEmailVerification(user, actionCodeSettings)
          .then(function () {
            message.success(t("notifications.invite_link_sent"), 8);
            // Remove invitedUser from firestore
            const invitedUserDocRef = doc(firestore, "invitedUsers", invitedUserId);
            deleteDoc(invitedUserDocRef)
              .then(() => {
                signOut(auth);
                navigate(login);
              })
              .catch((error) => console.log("Error deleting invited user: ", error));
          })
          .catch(function (error) {
            setLoading(false);
            console.error(error);
          });
      });
    } else {
      message.warning(t("notifications.invite_invalid_email"));
    }
  };

  const updateUserInFirestore = async (userId) => {
    try {
      const userDocRef = doc(firestore, "users", userId);
      await updateDoc(userDocRef, { userId: userId });
    } catch (error) {}
  };

  if (invitedUsersLoading) {
    return <Loading />;
  }

  return (
    <div className="h-100" style={backgroundStyle}>
      <div className="container d-flex flex-column justify-content-center h-100">
        {validLink ? (
          <Row justify="center">
            <Col xs={20} sm={20} md={20} lg={9}>
              <Card>
                <div className="my-2">
                  <div className="text-center">
                    <img className="img-fluid w-75" src="/img/logo.png" alt="" />
                    <h3 className="mt-3 font-weight-bold">
                      {t("accept_invite")}
                      {COMPANY_CONSTANTS.WEBSITE_NAME}
                    </h3>
                    <p className="mb-4">{t("form.enter_email")}</p>
                  </div>
                  <Row justify="center">
                    <Col xs={24} sm={24} md={20} lg={20}>
                      <Form form={form} layout="vertical" name="forgot-password" onFinish={onSubmit}>
                        <Form.Item
                          name="email"
                          onChange={(e) => setEmail(e.target.value)}
                          rules={[
                            {
                              required: true,
                              message: t("form.enter_email"),
                            },
                            {
                              type: "email",
                              message: t("form.enter_valid_email"),
                            },
                          ]}
                        >
                          <Input placeholder={t("email")} prefix={<MailOutlined className="text-primary" />} />
                        </Form.Item>
                        <Form.Item
                          name="password"
                          onChange={(e) => setPassword(e.target.value)}
                          rules={[
                            {
                              required: true,
                              message: t("form.enter_password"),
                            },
                          ]}
                        >
                          <Input
                            placeholder={t("password")}
                            type="password"
                            prefix={<LockOutlined className="text-primary" />}
                          />
                        </Form.Item>
                        <Form.Item>
                          <Button loading={loading} type="primary" htmlType="submit" block>
                            {loading ? t("registering") : t("register")}
                          </Button>
                        </Form.Item>
                      </Form>
                      <Link to={login}>{t("to_login")}</Link>
                    </Col>
                  </Row>
                </div>
              </Card>
            </Col>
          </Row>
        ) : (
          <Row justify="center">
            <Col xs={20} sm={20} md={20} lg={9}>
              <Card>
                <div className="text-center">
                  <h3 className="mt-3 font-weight-bold">{t("invalid_link_title")}</h3>
                  <p className="mb-4">{t("invalid_link_description", { company: COMPANY_CONSTANTS.COMPANY_NAME })}</p>
                  <Link to={login}>{t("to_login")}</Link>
                </div>
              </Card>
            </Col>
          </Row>
        )}
      </div>
    </div>
  );
};

export default AcceptInvite;
